import { Component, Input, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { LaunchDarklyService } from '@services/launchdarkly.service';
import { Logger, LoggingService } from '@services/logging.service';
import { AutoUnsubscribe } from '../../../decorators/auto-unsubscribe';

/**
 * Compliance AI disclaimer banner shown at the bottom of a spreading step whose
 * functionality is powered by an LLM feature (AUTO-3408). Visibility is gated by
 * a single LaunchDarkly flag passed in via [flagKey]: the banner shows only when
 * that flag is enabled for the tenant, so each step is covered independently.
 *
 * The per-tenant flag set is only correct after LaunchDarkly `identify()`
 * resolves, which is signalled through `flagChange`. We therefore read the flag
 * on init AND on every `flagChange`, not just once - a single read at init can
 * land on the anonymous bootstrap flags on a hard refresh and miss the settled
 * per-tenant value.
 */
@Component({
  selector: 'app-ai-disclaimer-banner',
  templateUrl: './ai-disclaimer-banner.component.html',
  styleUrls: ['./ai-disclaimer-banner.component.scss'],
  standalone: false,
})
@AutoUnsubscribe('subsArr$')
export class AiDisclaimerBannerComponent implements OnInit {
  /** The LaunchDarkly flag that gates this step's banner. */
  @Input() flagKey: string = '';

  isEnabled = false;

  subsArr$: Subscription[] = [];

  private logger: Logger;

  constructor(
    private _launchDarklyService: LaunchDarklyService,
    private _loggingService: LoggingService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('AiDisclaimerBannerComponent');
  }

  ngOnInit(): void {
    // A missing flagKey means an LLM-gated step would silently never show its
    // compliance disclaimer - a real compliance gap if a consumer forgets the
    // binding. Fail loud (routes to Sentry/alerting) but do not crash the step.
    if (!this.flagKey) {
      this.logger.error(
        'AiDisclaimerBannerComponent rendered without a [flagKey]; the AI disclaimer will not display.',
      );
      return;
    }

    this.updateVisibility(this._launchDarklyService.flags);
    this.subsArr$.push(
      this._launchDarklyService.flagChange.subscribe(flags =>
        this.updateVisibility(flags as Record<string, unknown>),
      ),
    );
  }

  private updateVisibility(flags: Record<string, unknown>): void {
    this.isEnabled = !!flags?.[this.flagKey];
  }
}
